import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { employeeAPI, leaveAPI, authAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, User, Mail, Phone, MapPin, Calendar, Building2, Camera, Lock } from 'lucide-react';
import ImageCropper from '../../components/shared/ImageCropper';
import ChangePasswordDialog from '../../components/shared/ChangePasswordDialog';


const MyProfile = () => {
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);


  useEffect(() => {
    if (user?.employee_id) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const response = await employeeAPI.getEmployee(user.employee_id);
      setEmployee(response.data);
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);

    // Clear input so same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = async (croppedBlob) => {
    const formData = new FormData();
    formData.append('file', croppedBlob, 'profile.jpg');

    setUploading(true);
    try {
      const response = await employeeAPI.uploadProfilePicture(user.employee_id, formData);
      setEmployee({ ...employee, profile_picture: response.data.profile_picture });
      toast.success('Profile picture updated');
    } catch (error) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        Profile not found
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Profile</h1>
          <p className="text-muted-foreground">View your profile information</p>
        </div>
        <Button variant="outline" onClick={() => setChangePasswordOpen(true)}>
          <Lock className="mr-2 h-4 w-4" />
          Change Password
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Card */}
        <Card className="lg:col-span-1">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <div className="relative group cursor-pointer" onClick={() => document.getElementById('photo-upload').click()}>
                <div className="h-24 w-24 rounded-full bg-primary flex items-center justify-center mb-4 overflow-hidden border-4 border-background shadow-lg">
                  {employee.profile_picture ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${employee.profile_picture}`}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl font-bold text-primary-foreground tracking-tighter">
                      {employee.first_name?.[0]?.toUpperCase()}{employee.last_name?.[0]?.toUpperCase()}
                    </span>
                  )}
                  {uploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
                      <Loader2 className="h-8 w-8 animate-spin text-white" />
                    </div>
                  )}
                </div>
                {!uploading && (
                  <div className="absolute bottom-4 right-0 p-1.5 bg-background rounded-full border shadow-sm group-hover:bg-accent transition-colors">
                    <Camera className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                )}
                <input
                  type="file"
                  id="photo-upload"
                  className="hidden"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                />
              </div>
              <h2 className="text-xl font-semibold">{employee.first_name} {employee.last_name}</h2>
              <p className="text-muted-foreground">{employee.designation || 'No designation'}</p>
              <Badge className="mt-2">{employee.status}</Badge>
              <div className="w-full mt-6 space-y-3 text-left">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{employee.department || 'No department'}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Joined {new Date(employee.date_of_joining).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium">{employee.first_name} {employee.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Employee Code</p>
                  <p className="font-medium font-mono">{employee.employee_code}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">{employee.date_of_birth || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gender</p>
                  <p className="font-medium">{employee.gender || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Personal Email</p>
                  <p className="font-medium">{employee.personal_email || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Blood Group</p>
                  <p className="font-medium">{employee.blood_group || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Emergency Contact</p>
                  <p className="font-medium">
                    {employee.emergency_contact ? (
                      typeof employee.emergency_contact === 'object' ? (
                        `${employee.emergency_contact.name || ''}${employee.emergency_contact.relationship ? ` (${employee.emergency_contact.relationship})` : ''}${employee.emergency_contact.phone ? ` - ${employee.emergency_contact.phone}` : ''}` || 'Not provided'
                      ) : (
                        employee.emergency_contact
                      )
                    ) : (
                      'Not provided'
                    )}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{employee.address || 'Not provided'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Employment Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Department</p>
                  <p className="font-medium">{employee.department || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Designation</p>
                  <p className="font-medium">{employee.designation || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Employment Type</p>
                  <p className="font-medium">{employee.employment_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Work Location</p>
                  <p className="font-medium">{employee.work_location || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reporting Manager</p>
                  <p className="font-medium">{employee.reporting_manager_name || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date of Joining</p>
                  <p className="font-medium">{new Date(employee.date_of_joining).toLocaleDateString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ID Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">PAN</p>
                  <p className="font-medium font-mono">{employee.pan || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aadhaar</p>
                  <p className="font-medium font-mono">
                    {employee.aadhaar ? `XXXX XXXX ${employee.aadhaar.slice(-4)}` : 'Not provided'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ImageCropper
        open={cropperOpen}
        onOpenChange={setCropperOpen}
        image={selectedImage}
        onCropComplete={handleCropComplete}
      />

      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </div>
  );
};


export default MyProfile;
